/**
 * PayslipPage.js — Full-page payslip detail view
 *
 * Route: /worker/:id/payslip/:payslipId
 *
 * Replaces the PayslipDetailModal drawer with a proper full-page layout so
 * there are no viewport/scroll constraints. Back button returns to the worker
 * profile via browser history (navigate(-1)) which preserves the active tab.
 *
 * Data strategy (same as the old modal):
 *   draft     — all sections fetched live from nested Directus relations
 *   approved / paid — earnings, deductions, payouts, attendance read from
 *                     frozen JSON snapshot fields; worker info always live
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatDate } from '../utils/helpers';
import { showAlert } from '../components/UI';
import {
  getPayslipDetail,
  getPayslipAttendance,
  recalculatePayslip,
  approvePayslip,
} from '../api/directus';
import MarkPaidModal from '../components/MarkPaidModal';
import { generatePayslipPDF } from '../utils/payslipPDF';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toIST(utcString) {
  if (!utcString) return '—';
  return new Date(utcString).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function generateDays(start, end) {
  if (!start || !end) return [];
  const days = [];
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  const cur  = new Date(sy, sm - 1, sd);
  const last = new Date(ey, em - 1, ed);
  while (cur <= last) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    days.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function formatDayLabel(dateStr) {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const date = new Date(y, mo - 1, d);
  return `${String(d).padStart(2, '0')} ${date.toLocaleDateString('en-IN', { month: 'short' })}, ${date.toLocaleDateString('en-IN', { weekday: 'short' })}`;
}

function parseSnapshot(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
}

function minsToHrs(mins) {
  if (!mins) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const STATUS_PILL = {
  draft:      'pill pill-pending',
  approved:   'pill pill-approved',
  paid:       'pill pill-paid',
  present:    'pill pill-present',
  checkout:   'pill pill-present',
  absent:     'pill pill-absent',
  break:      'pill pill-present',
  leave:      'pill pill-leave',
  weekly_off: 'pill pill-off',
  unmarked:   'pill pill-pending',
};

const STATUS_LABEL = {
  draft:      'Draft',
  approved:   'Approved',
  paid:       'Paid',
  present:    'Present',
  checkout:   'Present',
  absent:     'Absent',
  break:      'Present',
  leave:      'Leave',
  weekly_off: 'Weekly Off',
  unmarked:   'Unmarked',
};

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PayslipPage() {
  const { id: workerId, payslipId } = useParams();
  const navigate  = useNavigate();
  const { isManager } = useAuth();

  const [payslip,        setPayslip]        = useState(null);
  const [liveAttendance, setLiveAttendance] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [recalcSaving,   setRecalcSaving]   = useState(false);
  const [approveSaving,  setApproveSaving]  = useState(false);
  const [markPaidOpen,   setMarkPaidOpen]   = useState(false);
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [exporting,      setExporting]      = useState(false);
  const menuRef = useRef(null);

  const fetchPayslip = useCallback(() => {
    setLoading(true);
    Promise.all([
      getPayslipDetail(payslipId),
      getPayslipAttendance(payslipId),
    ])
      .then(([ps, att]) => {
        setPayslip(ps.data);
        setLiveAttendance(att.data || []);
      })
      .catch(() => setPayslip(null))
      .finally(() => setLoading(false));
  }, [payslipId]);

  useEffect(() => { fetchPayslip(); }, [fetchPayslip]);

  // Close context menu on outside click
  useEffect(() => {
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const isDraft = payslip?.status === 'draft';

  // Attendance always uses the live fetch (getPayslipAttendance) so punch
  // records are properly expanded via fields=*.*.* — fetching via the payslips
  // endpoint returns punch_in/out as bare FK integers, not objects.
  const attendanceArr  = liveAttendance;
  const earningsArr    = isDraft ? (Array.isArray(payslip?.earnings)   ? payslip.earnings   : []) : parseSnapshot(payslip?.earnings_snapshot);
  const deductionsArr  = isDraft ? (Array.isArray(payslip?.deductions) ? payslip.deductions : []) : parseSnapshot(payslip?.deductions_snapshot);
  const payoutsArr     = isDraft ? (Array.isArray(payslip?.payouts)    ? payslip.payouts    : []) : parseSnapshot(payslip?.payouts_snapshot);

  const attByDate = {};
  attendanceArr.forEach(r => { if (r.date) attByDate[r.date] = r; });

  const earningsByDate = {};
  earningsArr.forEach(r => { if (!r.date) return; (earningsByDate[r.date] = earningsByDate[r.date] || []).push(r); });

  const deductionsByDate = {};
  deductionsArr.forEach(r => { if (!r.date) return; (deductionsByDate[r.date] = deductionsByDate[r.date] || []).push(r); });

  const calendarDays = generateDays(payslip?.cycle_start, payslip?.cycle_end);
  const extraDates   = new Set([...earningsArr.map(r => r.date), ...deductionsArr.map(r => r.date)].filter(d => d && !calendarDays.includes(d)));
  const allDays      = [...calendarDays, ...Array.from(extraDates).sort()];

  const attSummary = attendanceArr.reduce((acc, r) => {
    const s = r.status;
    if (['checkout', 'present', 'break', 'approved'].includes(s)) acc.present++;
    else if (s === 'absent')     acc.absent++;
    else if (s === 'weekly_off') acc.off++;
    else if (s === 'leave')      acc.leave++;
    if (r.is_powerDay) acc.powerDays++;

    const pIn   = r.punch_in?.timestamp;
    const pOut  = r.punch_out?.timestamp;
    const bMins = r.break_duration || r.breaks?.reduce((s, b) => s + (b.duration || 0), 0) || 0;
    const sMins = r.shift_duration || (() => {
      if (!pIn || !pOut) return 0;
      return Math.max(0, Math.round((new Date(pOut) - new Date(pIn)) / 60000) - bMins);
    })();
    acc.totalShiftMins += sMins;
    return acc;
  }, { present: 0, absent: 0, off: 0, leave: 0, powerDays: 0, totalShiftMins: 0 });

  // Total Paid Hrs — sum wage_hours + OT_hours from earnings records (stored in hours)
  const totalPaidHrs = earningsArr.reduce(
    (sum, e) => sum + parseFloat(e.wage_hours || 0) + parseFloat(e.OT_hours || 0), 0
  );

  const workerObj = payslip?.worker;

  // ── Actions ───────────────────────────────────────────────────────────────

  async function handleRecalculate() {
    setMenuOpen(false);
    setRecalcSaving(true);
    try { await recalculatePayslip(payslipId); fetchPayslip(); }
    catch { showAlert('Recalculation failed. Please try again.', 'error'); }
    finally { setRecalcSaving(false); }
  }

  async function handleApprove() {
    setMenuOpen(false);
    setApproveSaving(true);
    try { await approvePayslip(payslipId); fetchPayslip(); }
    catch (err) { showAlert(err?.errors?.[0]?.message || 'Approval failed. Check data and retry.', 'error'); }
    finally { setApproveSaving(false); }
  }

  async function handleExportPDF() {
    setMenuOpen(false);
    setExporting(true);
    try {
      const doc = await generatePayslipPDF(
        workerObj,
        payslip,
        attendanceArr,
        earningsArr,
        deductionsArr,
        payoutsArr,
      );
      const safeName = (workerObj?.name || 'Worker').replace(/\s+/g, '-');
      const filename = `Payslip_${safeName}_${payslip.cycle_label}.pdf`;
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768;
      if (isMobile && navigator.share) {
        try {
          const blob = doc.output('blob');
          const file = new File([blob], filename, { type: 'application/pdf' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: `Payslip ${payslip.cycle_label}` });
            return;
          }
        } catch {
          // share failed or unsupported — fall through to download
        }
      }
      doc.save(filename);
    } catch (err) {
      showAlert('PDF generation failed. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="app-shell">

      {/* Sticky top nav */}
      <header className="pf-header" style={{ padding: '12px 16px' }}>
        <div className="pf-back-row">
          <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back">
            <span className="material-symbols-rounded">arrow_back</span>
          </button>
          <h1>Payslip</h1>
        </div>
        <div className="pf-header-actions">
          {payslip && (
            <span className={STATUS_PILL[payslip.status] || 'pill pill-pending'}>
              {STATUS_LABEL[payslip.status] || payslip.status}
            </span>
          )}
          {isManager && payslip && (
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                className="icon-btn"
                aria-label="More actions"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(o => !o)}
              >
                <span className="material-symbols-rounded">more_vert</span>
              </button>
              {menuOpen && (
                <div className="dash-popover psd-action-menu">
                  {payslip.status === 'draft' && (
                    <>
                      <button onClick={handleRecalculate} disabled={recalcSaving}>
                        <span className="material-symbols-rounded">refresh</span>
                        {recalcSaving ? 'Recalculating…' : 'Recalculate'}
                      </button>
                      <button onClick={handleApprove} disabled={approveSaving}>
                        <span className="material-symbols-rounded">check_circle</span>
                        {approveSaving ? 'Approving…' : 'Approve'}
                      </button>
                    </>
                  )}
                  {payslip.status === 'approved' && (
                    <button onClick={() => { setMenuOpen(false); setMarkPaidOpen(true); }}>
                      <span className="material-symbols-rounded">payments</span>
                      Mark Paid
                    </button>
                  )}
                  <hr />
                  <button onClick={handleExportPDF} disabled={exporting}>
                    <span className="material-symbols-rounded">picture_as_pdf</span>
                    {exporting ? 'Generating…' : 'Export PDF'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {loading ? (
        <div className="loader">Loading…</div>
      ) : !payslip ? (
        <div className="loader">Payslip not found.</div>
      ) : (
        <div className="page" style={{ maxWidth: 860 }}>

          {/* ── Document header ── */}
          <div className="psd-head" style={{ borderRadius: 14, border: '1px solid var(--color-neutral-100)', marginBottom: 0 }}>
            <p className="psd-company">Innomalous Pet Products</p>
            <p className="psd-id">Payslip #{workerObj?.id}/{payslip.cycle_label}</p>
            <p className="psd-worker-name">{workerObj?.name}</p>
            <div className="psd-meta">
              {workerObj?.doj && <span>Joined: {formatDate(workerObj.doj)}</span>}
              <span>Period: {formatDate(payslip.cycle_start)} – {formatDate(payslip.cycle_end)}</span>
              <span>Payout: {formatDate(payslip.payout_date)}</span>
            </div>
          </div>

          {/* ── Attendance summary ── */}
          <div className="psd-section">
            <p className="psd-section-title">Attendance Summary</p>
            <div className="psd-stats-bar">
              <div className="psd-stat">
                <span className="psd-stat-num" style={{ color: '#4d6325' }}>{attSummary.present}</span>
                <span className="psd-stat-label">Present</span>
              </div>
              {attSummary.absent > 0 && (
                <div className="psd-stat">
                  <span className="psd-stat-num" style={{ color: '#8a2f1a' }}>{attSummary.absent}</span>
                  <span className="psd-stat-label">Absent</span>
                </div>
              )}
              {attSummary.off > 0 && (
                <div className="psd-stat">
                  <span className="psd-stat-num" style={{ color: '#4e4480' }}>{attSummary.off}</span>
                  <span className="psd-stat-label">Weekly Off</span>
                </div>
              )}
              {attSummary.leave > 0 && (
                <div className="psd-stat">
                  <span className="psd-stat-num" style={{ color: '#2a506a' }}>{attSummary.leave}</span>
                  <span className="psd-stat-label">Leave</span>
                </div>
              )}
              {attSummary.powerDays > 0 && (
                <div className="psd-stat">
                  <span className="psd-stat-num" style={{ color: '#c79221' }}>{attSummary.powerDays}</span>
                  <span className="psd-stat-label">Power Days ⚡</span>
                </div>
              )}
              <div className="psd-stat">
                <span className="psd-stat-num">{(attSummary.totalShiftMins / 60).toFixed(1)}h</span>
                <span className="psd-stat-label">Total Worked Hrs</span>
              </div>
            </div>
          </div>

          {/* ── Day-by-day breakdown ── */}
          <div className="psd-section">
            <p className="psd-section-title">Attendance Details</p>
            <div style={{ overflowX: 'auto' }}>
              <table className="psd-day-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>In</th>
                    <th>Out</th>
                    <th>Productive</th>
                    <th>Break</th>
                    <th>Earnings</th>
                    <th>Deductions</th>
                  </tr>
                </thead>
                <tbody>
                  {allDays.map(date => {
                    const att    = attByDate[date];
                    const erns   = earningsByDate[date]   || [];
                    const deds   = deductionsByDate[date] || [];
                    const status = att?.status || null;
                    const isOff  = status === 'absent' || status === 'weekly_off' || status === 'leave';
                    const punchIn  = att?.punch_in?.timestamp  ?? null;
                    const punchOut = att?.punch_out?.timestamp ?? null;
                    const breakMins = att?.break_duration
                      || att?.breaks?.reduce((s, b) => s + (b.duration || 0), 0)
                      || 0;
                    const shiftMins = att?.shift_duration || (() => {
                      if (!punchIn || !punchOut) return 0;
                      return Math.max(0, Math.round((new Date(punchOut) - new Date(punchIn)) / 60000) - breakMins);
                    })();
                    return (
                      <tr key={date} className={isOff ? 'psd-row-off' : ''}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {formatDayLabel(date)}{att?.is_powerDay ? ' ⚡' : ''}
                        </td>
                        <td>
                          {status
                            ? <span className={STATUS_PILL[status] || 'pill pill-pending'} style={{ fontSize: 11, padding: '2px 7px' }}>{STATUS_LABEL[status] || status}</span>
                            : '—'}
                        </td>
                        <td>{isOff ? '—' : toIST(punchIn)}</td>
                        <td>{isOff ? '—' : toIST(punchOut)}</td>
                        <td>{isOff ? '—' : minsToHrs(shiftMins)}</td>
                        <td>{isOff ? '—' : minsToHrs(breakMins)}</td>
                        <td>
                          {erns.length > 0 ? (
                            <div className="psd-ledger">
                              {erns.map((e, i) => (
                                <React.Fragment key={i}>
                                  <span className="psd-ledger-amt earning">{formatCurrency(e.amount)}</span>
                                  <span className="psd-ledger-type">{e.type}</span>
                                </React.Fragment>
                              ))}
                              {erns.length > 1 && (
                                <React.Fragment>
                                  <span className="psd-ledger-sep" /><span className="psd-ledger-sep" />
                                  <span className="psd-ledger-amt earning psd-ledger-bold">{formatCurrency(erns.reduce((s, e) => s + parseFloat(e.amount || 0), 0))}</span>
                                  <span className="psd-ledger-type psd-ledger-bold">Total</span>
                                </React.Fragment>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td>
                          {deds.length > 0 ? (
                            <div className="psd-ledger">
                              {deds.map((d, i) => (
                                <React.Fragment key={i}>
                                  <span className="psd-ledger-amt deduction">{formatCurrency(d.amount)}</span>
                                  <span className="psd-ledger-type">{d.type}</span>
                                </React.Fragment>
                              ))}
                              {deds.length > 1 && (
                                <React.Fragment>
                                  <span className="psd-ledger-sep" /><span className="psd-ledger-sep" />
                                  <span className="psd-ledger-amt deduction psd-ledger-bold">{formatCurrency(deds.reduce((s, d) => s + parseFloat(d.amount || 0), 0))}</span>
                                  <span className="psd-ledger-type psd-ledger-bold">Total</span>
                                </React.Fragment>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {allDays.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: '16px 0' }}>No data available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Financials ── */}
          <div className="psd-section">
            <p className="psd-section-title">Financials</p>
            <div className="psd-fin">

              {/* Earnings & Deductions sub-section */}
              <p className="psd-fin-subhead">Earnings &amp; Deductions</p>
              {(payslip.opening_balance !== 0 && payslip.opening_balance != null) && (
                <div className="psd-fin-row">
                  <span>Previous Balance</span>
                  <span>{formatCurrency(payslip.opening_balance)}</span>
                </div>
              )}
              <div className="psd-fin-row">
                <span>+ Total Earnings</span>
                <span>{formatCurrency(payslip.total_earnings)}</span>
              </div>
              <div className="psd-fin-row">
                <span>− Total Deductions</span>
                <span>{formatCurrency(payslip.total_deductions)}</span>
              </div>
              <div className="psd-fin-row psd-fin-total">
                <span>Gross Payable</span>
                <span>{formatCurrency(parseFloat(payslip.opening_balance || 0) + parseFloat(payslip.total_earnings || 0) - parseFloat(payslip.total_deductions || 0))}</span>
              </div>

              {/* Payments sub-section */}
              {payoutsArr.length > 0 && (
                <>
                  <p className="psd-fin-subhead" style={{ marginTop: 18 }}>Payments</p>
                  <table className="psd-payout-table">
                    <tbody>
                      {payoutsArr.map((p, i) => (
                        <tr key={p.id || i}>
                          <td>{formatDate(p.date)}</td>
                          <td style={{ color: 'var(--fg-muted)', textTransform: 'capitalize' }}>{p.mode}</td>
                          <td className="amt">{formatCurrency(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="psd-fin-row psd-fin-total">
                    <span>Total Payments</span>
                    <span>{formatCurrency(payslip.total_payouts)}</span>
                  </div>
                </>
              )}

              {/* Round-off adjustment (shown only when non-zero) */}
              {!!parseFloat(payslip.roundOff_amount) && (
                <div className="psd-fin-row" style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
                  <span>Round-off</span>
                  <span>{formatCurrency(payslip.roundOff_amount)}</span>
                </div>
              )}

              {/* Net Due */}
              <div className="psd-fin-row psd-fin-total psd-fin-net-due">
                <span>Net Due</span>
                <span>{formatCurrency(payslip.net_due)}</span>
              </div>

            </div>
          </div>

        </div>
      )}

      {markPaidOpen && payslip && (
        <MarkPaidModal
          payslip={payslip}
          worker={workerObj}
          onClose={() => setMarkPaidOpen(false)}
          onSaved={() => { setMarkPaidOpen(false); fetchPayslip(); }}
        />
      )}
    </div>
  );
}
